import type { TurnstileRenderParams } from '../types/turnstile';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const CONTAINER_ID = 'cf-turnstile-container';
const EXECUTE_TIMEOUT_MS = 5000;
const WIDGET_READY_TIMEOUT_MS = 5000;

export const TURNSTILE_WIDGET_ID = 'chessMasters';

let initialized = false;
let widgetId: string | null = null;
let pending: ((token: string | null) => void) | null = null;
let ready = false;
let readyWaiters: Array<() => void> = [];

function settleReadiness() {
    if (ready) return;

    ready = true;

    const waiters = readyWaiters;
    readyWaiters = [];

    waiters.forEach((waiter) => waiter());
}

function waitForReady(timeoutMs: number): Promise<void> {
    if (ready) return Promise.resolve();

    return new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, timeoutMs);
        readyWaiters.push(() => {
            clearTimeout(timer);
            resolve();
        });
    });
}

function siteKey(): string | undefined {
    return process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
}

function resolvePending(token: string | null) {
    const resolve = pending;
    pending = null;

    resolve?.(token);
}

function rearmWidget() {
    try {
        if (widgetId) window.turnstile?.reset(widgetId);
    } catch {}
}

function renderWidget() {
    try {
        const params: TurnstileRenderParams = {
            sitekey: siteKey() as string,
            execution: 'execute',
            appearance: 'execute',
            callback: (token: string) => resolvePending(token),
            'error-callback': () => resolvePending(null),
            'timeout-callback': () => resolvePending(null),
        };

        widgetId = window.turnstile?.render('#' + CONTAINER_ID, params) ?? null;
        rearmWidget();
    } catch {
        widgetId = null;
    }
    settleReadiness();
}

export function __renderForTest() {
    if (!document.getElementById(CONTAINER_ID)) {
        const element = document.createElement('div');
        element.id = CONTAINER_ID;

        document.body.appendChild(element);
    }

    renderWidget();
}

export function initTurnstile(): void {
    if (initialized || typeof window === 'undefined' || !siteKey()) return;

    initialized = true;

    try {
        const container = document.createElement('div');
        container.id = CONTAINER_ID;
        container.style.cssText =
            'position:fixed;left:-9999px;top:0;width:300px;height:65px;opacity:0;pointer-events:none;';
        document.body.appendChild(container);

        const script = document.createElement('script');
        script.src = SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = renderWidget;
        script.onerror = settleReadiness;
        document.head.appendChild(script);
    } catch {}
}

let executionTail: Promise<unknown> | null = null;

export function getCaptchaToken(action: string): Promise<string | null> {
    const result = executionTail
        ? executionTail.then(() => executeForToken(action))
        : executeForToken(action);

    const tail = result
        .then(
            () => undefined,
            () => undefined
        )
        .then(() => {
            if (executionTail === tail) executionTail = null;
        });
    executionTail = tail;

    return result;
}

async function executeForToken(action: string): Promise<string | null> {
    if (!siteKey() || typeof window === 'undefined') {
        return null;
    }
    if (!ready) {
        await waitForReady(WIDGET_READY_TIMEOUT_MS);
    }
    if (!widgetId || !window.turnstile) {
        return null;
    }

    return new Promise<string | null>((resolve) => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        let retried = false;

        const fire = () => {
            try {
                window.turnstile!.execute('#' + CONTAINER_ID, { action });
            } catch {
                settle(null);
            }
        };

        const onTimeout = () => {
            if (timer === null) return;

            if (!retried) {
                retried = true;
                timer = setTimeout(onTimeout, EXECUTE_TIMEOUT_MS);
                fire();
                return;
            }
            settle(null);
        };

        const settle = (token: string | null) => {
            if (timer === null) return;

            clearTimeout(timer);
            timer = null;

            if (pending === settle) pending = null;

            resolve(token);
            rearmWidget();
        };

        pending = settle;
        timer = setTimeout(onTimeout, EXECUTE_TIMEOUT_MS);

        fire();
    }).catch(() => null);
}
